// src/components/common/FormViewManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Printer } from "lucide-react";
import { useNavigate } from "react-router-dom";
import FileList from "../papers/FileList";
import ApprovalLine_view from "../../approval/ApprovalLine_view";
import ApprovalAction from "../../approval/ApprovalAction";
import * as designMeta from "../../../api/design/meta";
import * as approvalMeta from "../../../api/approval/meta";
import { viewApprovalLine } from "../../../api/approval";
import { getSafetyDocDetail } from "../../../api/safety";
import PaperShell from "../layout/PaperShell";
import ApprovalLine_print from "../../approval/ApprovalLine_print";
import {
  getWorkReportDetail,
  getProgressDetail,
  getWorkOrderDetail,
  getCompletionReportDetail,
} from "../../../api/const";
import { getProjectName } from "../../../api/project";
import { softDeleteDoc } from "../../../api/papers";
import ConfirmDialog from "../ui/ConfirmModal";
import { getQualityMeta } from "../../../api/quality/meta";
import { getMaterialDoc } from "../../../api/material/meta";
import TableCell from "../ui/TableCell";
import FormViewPrint from "../print/FormViewPrint";

export default function FormViewManager({
  params,
  children,
  doc_name,
  doc_eng,
  app_top = false,
  app_action = false,
  print = false,
  onPrintReady,
}) {
  const navigate = useNavigate();
  const { project, dns, module, doc_type } = params || {};

  const [projectName, setProjectName] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [data, setData] = useState(null);
  const [approvalLines, setApprovalLines] = useState([]);
  const [loading, setLoading] = useState(false);

  /* 로그인 사용자 */
  const me = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  /* 문서 삭제 */
  const handleDelete = async () => {
    try {
      await softDeleteDoc(dns, true);
      alert("문서가 삭제되었습니다.");
      navigate(-1);
    } catch (e) {
      console.error(e);
      alert("삭제 실패");
    }
  };

  /* 프로젝트 이름 조회 */
  useEffect(() => {
    const load = async () => {
      const res = await getProjectName(project);
      setProjectName(res.name);
    };
    load();
  }, [project]);

  /* 문서 + 결재선 조회 */
  useEffect(() => {
    if (!dns) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        let res;

        if (module === "design") {
          res = await designMeta.getMeta({ dns });
        } else if (module === "quality") {
          res = await getQualityMeta({ dns });
        } else if (module === "approval") {
          res = await approvalMeta.getMeta(dns);
        } else if (module === "safety") {
          res = { data: await getSafetyDocDetail(dns) };
        } else if (module === "const") {
          if (doc_type === "DAILY") {
            res = { data: await getWorkReportDetail(dns) };
          } else if (doc_type === "weekly" || doc_type === "progress") {
            res = { data: await getProgressDetail(dns) };
          } else if (doc_type === "workorder") {
            res = { data: await getWorkOrderDetail(dns) };
          } else if (doc_type === "ordercomplete") {
            res = { data: await getCompletionReportDetail(dns) };
          }
        } else if (module === "material") {
          res = { data: await getMaterialDoc({ dns, project }) };
        }

        setData(res.data || res);

        console.log(res, "@@");

        if (doc_type !== "QC") {
          const approval = await viewApprovalLine(dns);
          setApprovalLines(approval.data.approval_lines || []);
        } else {
          setApprovalLines([]);
        }
      } catch (err) {
        console.error("FormViewManager error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dns, module, project]);

  /* 작성자 체크 */
  const getCreatedBy = useMemo(() => {
    if (!data) return null;
    return data.created_by || data.doc?.created_by || null;
  }, [data]);

  const meIsOwner = useMemo(() => {
    if (!getCreatedBy?.id || !me?.id) return false;
    return String(getCreatedBy.id) === String(me.id);
  }, [getCreatedBy, me]);

  const ALLOW_EDIT_STATES = [undefined, null, "", "none", "submit"];

  const canEdit = useMemo(() => {
    const status = data?.status || data?.meta?.status;
    return meIsOwner && ALLOW_EDIT_STATES.includes(status);
  }, [data, meIsOwner]);

  /* ---------------------- 화면 렌더링 ---------------------- */
  if (loading)
    return (
      <div className="p-6 bg-white rounded-xl shadow-sm text-gray-400 text-center">
        불러오는 중...
      </div>
    );

  if (!data)
    return (
      <div className="p-6 bg-white rounded-xl shadow-sm text-center text-gray-400">
        데이터를 불러올 수 없습니다.
      </div>
    );

  const meta = data.meta || {};

  return (
    <div className="flex justify-center items-start px-6 py-10 min-h-screen">
      <div className="flex flex-col lg:flex-row items-start justify-center gap-8 w-full max-w-[1600px] mx-auto">
        {/* -------------------- 좌측 문서 -------------------- */}
        <div>
          {/* 상단 버튼 (화면 전용) */}
          <div className="no-print flex items-center justify-between mb-4 pt-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft size={16} /> 목록으로
            </button>

            <div className="flex items-center gap-2">
              {canEdit && (
                <>
                  <button
                    onClick={() => {
                      // 모듈별로 올바른 수정 경로 생성
                      const currentPath = window.location.pathname;
                      let editUrl;

                      if (currentPath.includes("/quality/")) {
                        // Quality 모듈: /quality/{context}/{dns}/view → /quality/{mapped_context}/{dns}
                        const match = currentPath.match(
                          /\/quality\/([^\/]+)\/([^\/]+)\/view$/
                        );
                        if (match) {
                          const [, context, dns] = match;

                          // Context 매핑: MI/PC/NC/CA → inspection/checklist/ncr/car
                          const contextMap = {
                            MI: "inspection",
                            mi: "inspection",
                            INSPECTION: "inspection",
                            inspection: "inspection",
                            PC: "checklist",
                            pc: "checklist",
                            CHECKLIST: "checklist",
                            checklist: "checklist",
                            NC: "ncr",
                            nc: "ncr",
                            NCR: "ncr",
                            ncr: "ncr",
                            CA: "car",
                            ca: "car",
                            CAR: "car",
                            car: "car",
                          };

                          const mappedContext =
                            contextMap[context] || context.toLowerCase();
                          editUrl = `/project/${params.project}/quality/${mappedContext}/${dns}`;
                        }
                      }

                      // 기본 동작: /view만 제거
                      if (!editUrl) {
                        editUrl = currentPath.replace(/\/view$/, "");
                      }

                      navigate(editUrl);
                    }}
                    className="btn-edit"
                  >
                    수정
                  </button>

                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="btn-delete"
                  >
                    삭제
                  </button>
                </>
              )}

              {/* 반려 상태일 때 수정 버튼 */}
              {data?.status === "rejected" && (
                <button
                  onClick={() => {
                    const editUrl = window.location.pathname.replace(
                      /\/view$/,
                      ""
                    );
                    navigate(editUrl);
                  }}
                  className="btn-edit"
                >
                  수정
                </button>
              )}

              <button
                onClick={() => window.print()}
                className="flex items-center gap-1 px-3 btn-basic"
              >
                <Printer size={16} /> 인쇄
              </button>
            </div>
          </div>

          {/* -------------------- 문서 내용 -------------------- */}
          <PaperShell>
            {/* 문서 헤더 */}
            <header
              className="doc-header pb-5 border-b mb-6 text-center"
              style={{ pageBreakInside: "avoid" }}
            >
              {(module === "approval" || app_top) && (
                <div className="flex justify-end mb-3">
                  <ApprovalLine_print lines={approvalLines} />
                </div>
              )}

              <div className="text-title">
                {doc_name || data.header?.title || data.title || "-"}
              </div>

              {doc_eng ? <div className="text-xs mt-1">({doc_eng})</div> : null}

              {/* ⭐⭐⭐ 기본 정보 테이블 (복원됨) ⭐⭐⭐ */}
              <div className=" mt-6">
                <table className="w-full table-fixed text-left">
                  <colgroup>
                    <col style={{ width: "90px" }} />
                    <col style={{ width: "40%" }} />
                    <col style={{ width: "90px" }} />
                    <col style={{ width: "40%" }} />
                  </colgroup>
                  <tbody>
                    <tr>
                      <TableCell
                        label="문서 번호"
                        colSpan={3}
                        value={data.doc_dns || dns || "-"}
                      />
                    </tr>
                    <tr>
                      <TableCell
                        label="현장명"
                        colSpan={3}
                        value={
                          data.project_name || projectName || project || "-"
                        }
                      />
                    </tr>
                    <tr>
                      <TableCell
                        label="작성자"
                        value={
                          data.created_by?.name ||
                          data.created_by_name ||
                          data.created_by ||
                          "-"
                        }
                      />
                      <TableCell
                        label="작성일"
                        value={
                          data.created_at
                            ? new Date(data.created_at).toLocaleDateString()
                            : meta.create_date
                            ? new Date(meta.create_date).toLocaleDateString()
                            : "-"
                        }
                      />
                    </tr>
                    <tr>
                      <TableCell
                        label="문서 제목"
                        colSpan={3}
                        value={data.header?.title || data.title || "-"}
                      />
                    </tr>
                  </tbody>
                </table>
              </div>
            </header>

            {/* 문서 본문 (항상 렌더) */}
            {typeof children === "function"
              ? children({ meta, data, project, approvalLines, me })
              : React.Children.map(children, (child) =>
                  React.isValidElement(child)
                    ? React.cloneElement(child, {
                        meta,
                        data,
                        project,
                        approvalLines,
                        me,
                      })
                    : child
                )}

            {/* 첨부 자료 (화면 전용) */}
            <div className="no-print">
              <h2 className="text-body-bold mb-1 mt-4">첨부자료</h2>
              <div className="border rounded-xl p-3">
                <FileList dns={dns} />
              </div>

              <div className="text-xs text-gray-400 mt-6 text-center">
                ※ 본 문서는 시스템에서 자동 생성된 전자문서 원본입니다.
              </div>
            </div>
          </PaperShell>
        </div>

        {/* -------------------- 우측 결재 패널 -------------------- */}
        {(module === "approval" || app_action) && (
          <div className="no-print flex-1 min-w-[280px] max-w-[400px] sticky top-24 self-start mt-4 print:hidden">
            {/* <div className="border p-2">
              <ApprovalLine_view lines={approvalLines} />
            </div> */}
            <ApprovalAction approvalLines={approvalLines} />
          </div>
        )}
      </div>

      {/* 삭제 모달 */}
      <ConfirmDialog
        open={showDeleteConfirm}
        title="문서 삭제"
        description="정말 이 문서를 삭제하시겠습니까? 삭제 후 복구할 수 없습니다."
        confirmText="삭제"
        cancelText="취소"
        onConfirm={() => {
          setShowDeleteConfirm(false);
          handleDelete();
        }}
        onCancel={() => setShowDeleteConfirm(false)}
        onClose={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}
